import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import type { FormStep } from '@/api';
import { formStepsProgressAtom } from '@/scenes/Room/atom';
import {
  AnswerBoxView,
  ChoicesView,
  FormStepsPill,
  PhoneFieldView,
  formatPhoneDigits,
  spokenPhoneDigits,
} from '@/scenes/Room/FormSteps';

// Dev only: the website-widget call screen (1024×615) with its form pieces, no LiveKit room needed.
// /dev/widget-call-preview?scene=choices|phone

export const Route = createFileRoute('/dev/widget-call-preview')({
  component: Preview,
  validateSearch: (search: Record<string, unknown>): { scene?: string } => ({
    scene: typeof search.scene === 'string' ? search.scene : undefined,
  }),
});

const STEPS: FormStep[] = [
  { key: 'background', label: 'Background', description: 'Your current status', goal: '' },
  { key: 'financials', label: 'Financials', description: 'Capital & budget', goal: '' },
  { key: 'timeline', label: 'Timeline & Goals', description: 'Your objectives', goal: '' },
  { key: 'commitment', label: 'Commitment', description: 'Readiness level', goal: '' },
  { key: 'contact', label: 'Contact Info', description: 'Where to reach you', goal: '' },
];

const SPOKEN = 'plus one four one five five five five zero one two three';

function Preview() {
  const { scene = 'choices' } = Route.useSearch();
  const setProgress = useSetAtom(formStepsProgressAtom);
  const [picked, setPicked] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  const [heard, setHeard] = useState('');

  useEffect(() => {
    setProgress(
      scene === 'phone'
        ? { current: 'contact', completed: ['background', 'financials', 'timeline', 'commitment'] }
        : { current: 'financials', completed: ['background'] },
    );
  }, [scene, setProgress]);

  // Phone scene: the words arrive one by one, as a live transcript would.
  useEffect(() => {
    if (scene !== 'phone') return;
    const words = SPOKEN.split(' ');
    let i = 0;
    setHeard('');
    const timer = window.setInterval(() => {
      i = i >= words.length + 4 ? 0 : i + 1;
      setHeard(words.slice(0, Math.min(i, words.length)).join(' '));
    }, 420);
    return () => window.clearInterval(timer);
  }, [scene]);

  const caption =
    scene === 'phone'
      ? 'Last step. What’s the best phone number for the team to call you?'
      : 'Next, roughly how much capital can you invest to launch?';
  const parsed = spokenPhoneDigits(heard);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-neutral-100 p-6">
      <div className="flex gap-2 text-sm">
        {['choices', 'phone'].map((key) => (
          <a
            key={key}
            href={`?scene=${key}`}
            className={`rounded-full px-3 py-1.5 ${scene === key ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-700'}`}>
            {key === 'choices' ? 'Tap an answer' : 'Phone number'}
          </a>
        ))}
      </div>
      {/* transform: the call's position:fixed pieces stay inside this 1024×615 frame */}
      <div
        className="relative h-[615px] w-[1024px] max-w-full overflow-hidden rounded-[20px] bg-black shadow-2xl"
        style={{ transform: 'translateZ(0)' }}>
        <FormStepsPill steps={STEPS} top="16px" />
        <p className="absolute inset-x-0 top-[22%] mx-auto max-w-[560px] px-6 text-center text-[22px] font-medium leading-snug text-white text-balance">
          {caption}
        </p>
        {scene === 'phone' ? (
          <div className="pointer-events-none fixed inset-x-0 top-[56%] z-[84] flex justify-center px-4">
            <PhoneFieldView
              label="Your phone number"
              value={formatPhoneDigits(parsed)}
              listening={parsed.digits.length < 11}
            />
          </div>
        ) : (
          <ChoicesView
            card={{
              question: 'How much capital can you invest to launch?',
              options: ['Under $25k', '$25k–$50k', '$50k–$100k', '$100k+', 'Not sure yet'],
            }}
            picked={picked}
            onPick={(option) => {
              setPicked(option);
              window.setTimeout(() => setPicked(null), 1600);
            }}
          />
        )}
        <AnswerBoxView
          value={answer}
          onChange={setAnswer}
          onSubmit={(event) => {
            event.preventDefault();
            setAnswer('');
          }}
          disabled={!answer.trim()}
        />
      </div>
    </div>
  );
}
